<template>
  <div>
    <technical-shutdown v-if="showTechnicalShutdownBanner" />
    <!-- User meta -->
    <div class="anz-mb-s">
      <div class="anz-text-uppercase anz-color-black-60 anz-mb-xs">
        {{ $translate('profile.labels.profile') }}
      </div>
      <div class="anz-flex anz-align-items-center">
        <div class="anz-fs-xl anz-fs-mx-mo anz-font-alternative">
          <span>{{ profile.nickname }}</span>
        </div>
        <a-premium
          size="anz-badge-md anz-ml-s"
          v-if="profile.isSubscriptionPremium"
        />
        <div class="anz-ml-auto anz-hidden-mo anz-flex" v-if="loggedIn">
          <div class="anz-mr-s" v-if="!isShowingCurrentUser">
            <a-button
              v-if="isUserBlocked"
              :title="$translate('profile.actions.removeBlockedUser')"
              class-name="anz-btn anz-btn-border anz-btn-icon"
              @click="unblockUser"
              :disabled="isBlocking"
              :loading="isBlocking"
              icon-width="1.0714em"
              icon-height="1.0714em"
              loader-class="anz-spinner-xs"
              icon-class="anz-mr-s"
            >
            </a-button>
            <a-button
              v-else
              :title="$translate('profile.actions.blockUser')"
              class-name="anz-btn anz-btn-border anz-btn-icon"
              @click="blockUser"
              :disabled="isBlocking"
              :loading="isBlocking"
              icon-width="1.0714em"
              icon-height="1.0714em"
              loader-class="anz-spinner-xs"
              icon-class="anz-mr-s"
            >
            </a-button>
          </div>
          <div
            v-if="!isShowingCurrentUser && loggedIn && isSubscribedInitialized"
          >
            <a-button
              v-if="currentUserSubscription"
              :title="$translate('profile.actions.removeSubscriber')"
              class-name="anz-btn anz-btn-border anz-btn-icon"
              @click="removeUserSubscription"
              :disabled="isRemovingUserSubscription"
              :loading="isRemovingUserSubscription"
              icon-width="1.0714em"
              icon-height="1.0714em"
              loader-class="anz-spinner-xs"
              icon-class="anz-mr-s"
            >
            </a-button>
            <a-button
              v-else
              :title="$translate('profile.actions.subscribeUser')"
              class-name="anz-btn anz-btn-primary anz-btn-icon"
              @click="subscribeUser"
              :disabled="isSubscribingUser"
              :loading="isSubscribingUser"
              icon-width="1.0714em"
              icon-height="1.0714em"
              loader-class="anz-spinner-xs"
              icon-class="anz-mr-s"
            >
            </a-button>
          </div>
          <a-button
            v-if="isShowingCurrentUser && loggedIn"
            add-href
            :href="generateUserEditRoute()"
            :title="$translate('profile.actions.editProfile')"
            class-name="anz-btn anz-btn-border"
          ></a-button>
        </div>
      </div>
    </div>
    <a-user-profile-meta-row css-class="anz-mb-s anz-fs-15" :background="true">
      <a-user-profile-meta-item v-if="isShowingCurrentUser">
        <template #title>
          {{ $translate('profile.profileMeta.subscriptionTitle') }}:
        </template>
        <template
          v-if="
            currentUser.isPayingCustomer && currentUser.isSubscriptionPremium
          "
          #value
        >
          {{ $translate('profile.profileMeta.subscriptionValuePremium') }}
        </template>
        <template v-else-if="currentUser.isPayingCustomer" #value>
          {{ $translate('profile.profileMeta.subscriptionValueStandard') }}
        </template>
        <template v-else #value>
          {{ $translate('profile.profileMeta.subscriptionValueNone') }}
        </template>
      </a-user-profile-meta-item>
      <a-user-profile-meta-item>
        <template #title>
          {{ $translate('profile.profileMeta.superLikesAwarded') }}:
        </template>
        <template #value>
          <a-icon
            name="star"
            width="0.875em"
            height="0.875em"
            class-name="anz-color-orange"
          />
          {{ profile.stats.superLikesReceivedTotal }}
        </template>
      </a-user-profile-meta-item>
      <a-user-profile-meta-item v-if="isShowingCurrentUser">
        <template #title>
          {{ $translate('profile.profileMeta.superLikesAvailable') }}:
        </template>
        <template #value>
          {{ profile.stats.superLikesRemaining }} /
          {{ profile.stats.superLikesEntitled }}
        </template>
      </a-user-profile-meta-item>
    </a-user-profile-meta-row>
    <a-user-profile-meta-row css-class="anz-mb-xl-sm anz-fs-13">
      <a-user-profile-meta-item>
        <template #title>
          {{ $translate('profile.labels.firstActivityDate') }}:
        </template>
        <template #value>
          {{ formatDateTime(profile.activity.firstActivityDate) }}
        </template>
      </a-user-profile-meta-item>
      <a-user-profile-meta-item>
        <template #title>
          {{ $translate('profile.labels.lastActivityDate') }}:
        </template>
        <template #value>
          {{ formatDateTime(profile.activity.lastActivityDate) }}
        </template>
      </a-user-profile-meta-item>
      <a-user-profile-meta-item v-if="profile.blog.link.length > 0">
        <template #title>
          {{ $translate('profile.labels.blogger') }}:
        </template>
        <template #value>
          <a-button
            add-href
            :href="generateBlogRoute(profile)"
            :title="profile.blog.link"
            className="anz-link anz-font-default anz-color-primary anz-link-hover-underline anz-text-break-all"
          />
        </template>
      </a-user-profile-meta-item>
      <a-user-profile-meta-item>
        <template #title> {{ $translate('profile.labels.rules') }}: </template>
        <template #value>
          <a-button
            add-href
            :href="generateCodexRoute()"
            :title="$translate('profile.actions.codexShort')"
            className="anz-link anz-color-primary anz-link-hover-underline"
            target="_blank"
          />
        </template>
      </a-user-profile-meta-item>
    </a-user-profile-meta-row>
    <div class="anz-hidden-sm anz-wf anz-mt-s anz-mb-l" v-if="loggedIn">
      <div class="anz-mb-xs" v-if="!isShowingCurrentUser">
        <a-button
          v-if="isUserBlocked"
          :title="$translate('profile.actions.removeBlockedUser')"
          class-name="anz-btn anz-btn-border anz-btn-icon anz-wf"
          @click="unblockUser"
          :disabled="isBlocking"
          :loading="isBlocking"
          icon-width="1.0714em"
          icon-height="1.0714em"
          loader-class="anz-spinner-xs"
          icon-class="anz-mr-s"
        >
        </a-button>
        <a-button
          v-else
          :title="$translate('profile.actions.blockUser')"
          class-name="anz-btn anz-btn-border anz-btn-icon anz-wf"
          @click="blockUser"
          :disabled="isBlocking"
          :loading="isBlocking"
          icon-width="1.0714em"
          icon-height="1.0714em"
          loader-class="anz-spinner-xs"
          icon-class="anz-mr-s"
        >
        </a-button>
      </div>
      <div v-if="!isShowingCurrentUser && loggedIn && isSubscribedInitialized">
        <a-button
          v-if="currentUserSubscription"
          :title="$translate('profile.actions.removeSubscriber')"
          class-name="anz-btn anz-btn-border anz-btn-icon anz-wf"
          @click="removeUserSubscription"
          :disabled="isRemovingUserSubscription"
          :loading="isRemovingUserSubscription"
          icon-width="1.0714em"
          icon-height="1.0714em"
          loader-class="anz-spinner-xs"
          icon-class="anz-mr-s"
        >
        </a-button>
        <a-button
          v-else
          :title="$translate('profile.actions.subscribeUser')"
          class-name="anz-btn anz-btn-primary anz-btn-icon anz-wf"
          @click="subscribeUser"
          :disabled="isSubscribingUser"
          :loading="isSubscribingUser"
          icon-width="1.0714em"
          icon-height="1.0714em"
          loader-class="anz-spinner-xs"
          icon-class="anz-mr-s"
        >
        </a-button>
      </div>
      <a-button
        v-if="isShowingCurrentUser && loggedIn"
        add-href
        :href="generateUserEditRoute()"
        :title="$translate('profile.actions.editProfile')"
        class-name="anz-btn anz-btn-border anz-wf"
      ></a-button>
    </div>
    <profile-api-error v-if="apiError" :api-error="apiError" />
    <user-profile-menu />
    <!-- Content -->
    <all-post-list v-if="activePage === ALL_POSTS" />
    <subscriber-post-list v-else-if="activePage === SUBSCRIBERS" />
    <hidden-post-list v-if="activePage === HIDDEN_POSTS" />
    <followers-post-list v-else-if="activePage === FOLLOWERS" />
    <unused-post-replies-list v-else-if="activePage === UNSEEN_REPLIES_POSTS" />
    <all-post-replies-list v-else-if="activePage === ALL_REPLIES_POSTS" />
    <blocked-user-list v-else-if="activePage === BLACKLIST" />
    <reported-post-list v-else-if="activePage === REPORTED" />
    <super-like-list v-else-if="activePage === SUPER_LIKE" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import useCurrentUser, { useProfileShow } from '@/composables/user/user'
import AButton from '@/components/Forms/AButton.vue'
import AIcon from '@/components/AIcon.vue'
import useProfileMenu, { ProfileMenu } from '@/composables/profile/profileMenu'
import useSubscriberManager, {
  useRemoveUserSubscription,
  useSubscribeUser,
} from '@/composables/subscribers/subscribersManager'
import { useRouter } from '@/routing/router'
import { DateTimeHelper } from '@/helper/dateTime'
import AllPostList from '@/components/Profile/Lists/AllPostList.vue'
import SubscriberPostList from '@/components/Profile/Lists/SubscribersPostList.vue'
import HiddenPostList from '@/components/Profile/Lists/HiddenPostList.vue'
import FollowersPostList from '@/components/Profile/Lists/FollowersPostList.vue'
import AllPostRepliesList from '@/components/Profile/Lists/AllPostRepliesList.vue'
import UnusedPostRepliesList from '@/components/Profile/Lists/UnseenPostRepliesList.vue'
import TechnicalShutdown from '@/components/TechnicalShutdown.vue'
import UserProfileMenu from '@/components/Profile/Menu/UserProfileMenu.vue'
import { useProfileUserBlock } from '@/composables/userBlock/userBlock'
import BlockedUserList from '@/components/Profile/Lists/BlockedUserList.vue'
import ReportedPostList from '@/components/Profile/ReportedPostList.vue'
import { usePostListUiActions } from '@/composables/ui/uiActions'
import ProfileApiError from '@/components/Profile/Error/ProfileApiError.vue'
import SuperLikeList from '@/components/Profile/SuperLikeList.vue'
import AUserProfileMetaRow from '@/components/Ui/Profile/UserProfileMetaRow.vue'
import AUserProfileMetaItem from '@/components/Ui/Profile/UserProfileMetaItem.vue'
import APremium from '@/components/Ui/Badge/Premium.vue'

export default defineComponent({
  name: 'UserProfile',
  components: {
    APremium,
    AUserProfileMetaItem,
    AUserProfileMetaRow,
    SuperLikeList,
    ProfileApiError,
    ReportedPostList,
    BlockedUserList,
    UserProfileMenu,
    TechnicalShutdown,
    UnusedPostRepliesList,
    AllPostRepliesList,
    FollowersPostList,
    HiddenPostList,
    SubscriberPostList,
    AllPostList,
    AButton,
    AIcon,
  },
  data() {
    return {
      SUBSCRIBERS: ProfileMenu.SUBSCRIBERS,
      FOLLOWERS: ProfileMenu.FOLLOWERS,
      ALL_POSTS: ProfileMenu.ALL_POSTS,
      HIDDEN_POSTS: ProfileMenu.HIDDEN_POSTS,
      UNSEEN_REPLIES_POSTS: ProfileMenu.UNSEEN_REPLIES_POSTS,
      ALL_REPLIES_POSTS: ProfileMenu.ALL_REPLIES_POSTS,
      BLACKLIST: ProfileMenu.BLACKLIST,
      REPORTED: ProfileMenu.REPORTED,
      SUPER_LIKE: ProfileMenu.SUPER_LIKE,
    }
  },
  methods: {
    formatDateTime(date: string) {
      return DateTimeHelper.formatDateTimeFromString(date)
    },
  },
  async setup() {
    const { currentUser, loggedIn } = useCurrentUser()
    const { profile, loadProfile, isShowingCurrentUser } = useProfileShow()
    const {
      isSubscribedInitialized,
      currentUserSubscription,
      fetchIsUserSubscribed,
    } = useSubscriberManager()

    await loadProfile()
    if (!isShowingCurrentUser.value && loggedIn.value) {
      fetchIsUserSubscribed(profile.value?.id ?? 0)
    }

    const { init } = useProfileMenu()
    init()

    return {
      profile,
      currentUser,
      isShowingCurrentUser,
      loggedIn,
      isSubscribedInitialized,
      currentUserSubscription,
      ...useSubscribeUser(profile.value?.id ?? 0),
      ...useRemoveUserSubscription(),
      ...useRouter(),
      ...useProfileMenu(),
      ...usePostListUiActions(),
      ...useProfileUserBlock(profile.value?.id ?? 0),
    }
  },
})
</script>
